import React from 'react';
import { Avatar, Button, Chip, Collapse, Link, makeStyles, Paper, TextField, Typography } from '@material-ui/core';
import { HomeWorkOutlined } from '@material-ui/icons';
import { Rating } from '@material-ui/lab';
import RatingStarIcon from 'assets/icons/svg/RatingStarIcon';
import { initials } from '../common/ratingUtils';
import { COMMENT_MAX_LENGTH, IEmployee, IEmployeeAnswer } from './personalRatingState';

const useStyles = makeStyles({
  block: { marginTop: 6 },
  card: { position: 'relative', padding: '12px 18px', borderRadius: 7 },
  flash: {
    position: 'absolute',
    left: -1,
    top: -1,
    bottom: -1,
    width: 4,
    borderRadius: '7px 0 0 7px',
    backgroundColor: '#e03131',
  },
  row: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  avatar: { width: 44, height: 44, fontSize: 14, fontWeight: 600, color: '#7b8494', backgroundColor: '#e4e9f1' },
  person: { flex: '1 1 160px', minWidth: 150 },
  name: { fontSize: 14, fontWeight: 600, color: '#111827' },
  position: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  skip: { fontSize: 11, color: '#8a93a0', borderBottom: '1px dashed #c9ced6', lineHeight: 1.5 },
  skipped: { fontSize: 11, fontWeight: 600, color: '#4b5563', lineHeight: 1.5 },
  comment: { marginTop: 8, paddingLeft: 58, boxSizing: 'border-box' },
  realEstates: {
    margin: '6px 0 8px 18px',
    padding: '12px 14px',
    backgroundColor: '#f7f9fc',
    border: '1px solid #dbe3ef',
    borderRadius: 6,
  },
  realEstatesTitle: { fontSize: 12, color: '#6b7280', marginBottom: 9 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { height: 24, backgroundColor: '#fff' },
});

interface IProps {
  id: string;
  employee: IEmployee;
  answer: IEmployeeAnswer;
  flash: boolean;
  showComment: boolean;
  realEstatesOpen: boolean;
  onToggleRealEstates(): void;
  onRate(rating: number): void;
  onToggleSkip(): void;
  onComment(comment: string): void;
  onCommentFocus(): void;
  onCommentBlur(): void;
}

const EmployeeCard = ({
  id,
  employee,
  answer,
  flash,
  showComment,
  realEstatesOpen,
  onToggleRealEstates,
  onRate,
  onToggleSkip,
  onComment,
  onCommentFocus,
  onCommentBlur,
}: IProps) => {
  const classes = useStyles();

  return (
    <div className={classes.block}>
      <Paper id={id} variant="outlined" className={classes.card}>
        {flash && <span className={classes.flash} />}
        <div className={classes.row}>
          <Avatar className={classes.avatar}>{initials(employee.fullName)}</Avatar>
          <div className={classes.person}>
            <Typography noWrap className={classes.name}>
              {employee.fullName}
            </Typography>
            <Typography noWrap className={classes.position}>
              {employee.positionName}
            </Typography>
          </div>

          <Link component="button" className={answer.skipped ? classes.skipped : classes.skip} onClick={onToggleSkip}>
            {answer.skipped ? 'Пропущен ✕' : 'Не могу оценить'}
          </Link>

          <Rating
            name={`${id}-rating`}
            value={answer.skipped ? null : answer.rating}
            disabled={answer.skipped}
            size="large"
            icon={<RatingStarIcon fontSize="inherit" />}
            onChange={(_, value) => {
              const picked = value ?? answer.rating;
              if (picked !== null) onRate(picked);
            }}
          />

          <Button
            size="small"
            color="primary"
            title="Объекты закрепления"
            startIcon={<HomeWorkOutlined />}
            onClick={onToggleRealEstates}
          >
            {employee.realEstates.length}
          </Button>
        </div>

        {showComment && (
          <TextField
            className={classes.comment}
            size="small"
            variant="outlined"
            fullWidth
            placeholder="Комментарий (не обязательно)"
            value={answer.comment}
            inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
            onChange={(event) => onComment(event.target.value)}
            onFocus={onCommentFocus}
            onBlur={onCommentBlur}
          />
        )}
      </Paper>

      <Collapse in={realEstatesOpen} unmountOnExit>
        <div className={classes.realEstates}>
          <Typography className={classes.realEstatesTitle}>Закреплен на объектах · оценка действует на всех</Typography>
          <div className={classes.chips}>
            {employee.realEstates.map(({ realEstateGuid, realEstateName }) => (
              <Chip
                key={realEstateGuid}
                size="small"
                variant="outlined"
                label={realEstateName}
                className={classes.chip}
              />
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default EmployeeCard;
